import Expense from "../../../classes/Expense";
import MileageLog from "../../../classes/MileageLog";
import { db, storage } from "../../../boot/firebase";
import {
  collection,
  deleteDoc,
  doc,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import { deleteObject, listAll, ref as storageRef } from "firebase/storage";

export const GET_EXPENSE = async ({ dispatch }, id) => {
  return dispatch("GET_DOC", {
    converter: Expense,
    path: Expense.getPath(id),
    stateKey: "expenses",
    id,
  });
};

export const GET_EXPENSES = ({ dispatch }, { where, limit, pagination }) => {
  return dispatch("GET_COLLECTION", {
    converter: Expense,
    stateKey: "expenses",
    path: Expense.getPath(),
    where,
    limit,
    pagination,
  });
};

export const LISTEN_EXPENSE = ({ dispatch }, { id, next, error, unlisten }) => {
  return dispatch("LISTEN_DOC", {
    converter: Expense,
    path: Expense.getPath(id),
    stateKey: "expenses",
    id,
    next,
    error,
    unlisten,
  });
};

export const LISTEN_EXPENSES = (
  { dispatch },
  { where, listenerPath, next, pagination, error, unlisten }
) => {
  return dispatch("LISTEN_COLLECTION", {
    converter: Expense,
    path: Expense.getPath(),
    stateKey: "expenses",
    listenerPath: listenerPath || "expenses",
    where,
    pagination,
    next,
    error,
    unlisten,
  });
};

export const LISTEN_SALE_EXPENSES = (
  { dispatch },
  { projectID, saleID, listenerPath, next, error, unlisten }
) => {
  const saleId = projectID + saleID;
  return dispatch("LISTEN_COLLECTION", {
    converter: Expense,
    path: Expense.getPath(),
    stateKey: "expenses",
    listenerPath: listenerPath || "sale-expenses-" + saleId,
    // Every status: pending expenses count toward the sale's actual cost too,
    // so the Accounting tabs must load and list them.
    where: [
      ["projectID", "==", projectID],
      ["saleID", "==", saleID],
    ],
    next,
    error,
    unlisten,
  });
};

export const SAVE_EXPENSE = async ({ dispatch, state }, expense) => {
  const folderPath = `expenses/${expense.id || "new"}`;

  const saveReceipt = async (existingData) => {
    return dispatch("SAVE_DOCUMENTS", {
      parentData: expense.data,
      fieldPath: "receipt",
      folderPath,
      isSingle: true,
      existingData,
    });
  };

  if (!expense.data.created) {
    // New Expense
    expense.data.created = new Date();
    expense.data.createdBy = state.user.id;
    expense.data.submittedBy = state.user.id;
    expense.data.submittedAt = new Date();

    const ref = doc(collection(db, Expense.getPath()));
    expense.id = ref.id;

    // Update folder path with actual ID and save receipt
    await dispatch("SAVE_DOCUMENTS", {
      parentData: expense.data,
      fieldPath: "receipt",
      folderPath: `expenses/${expense.id}`,
      isSingle: true,
    });

    await setDoc(ref.withConverter(Expense), expense);
  } else {
    // Existing Expense
    expense.data.modified = new Date();
    expense.data.modifiedBy = state.user.id;

    const current = await dispatch("GET_EXPENSE", expense.id);
    await saveReceipt(current.data);

    await setDoc(doc(db, expense.path).withConverter(Expense), expense);
  }
  return expense;
};

export const REVIEW_EXPENSE = async ({ commit, state }, { expense }) => {
  expense.data.status = "reviewed";
  expense.data.reviewedBy = state.user.id;
  expense.data.reviewedAt = new Date();
  expense.data.modified = new Date();
  expense.data.modifiedBy = state.user.id;

  await setDoc(doc(db, expense.path).withConverter(Expense), expense);

  // Expense costs are propagated to the linked sale's `total.additionalCost`
  // (and onward to commission `actualCost`) server-side by the `touchExpense`
  // Cloud Function (functions/expenses.js). The reviewer rarely has the sale
  // loaded in state, so this must not be done client-side.

  // If expense is linked to mileage logs, mark them as archived
  if (
    expense.data.mileageLogIds &&
    Array.isArray(expense.data.mileageLogIds) &&
    expense.data.mileageLogIds.length > 0
  ) {
    const now = new Date();
    const batch = writeBatch(db);
    for (const logId of expense.data.mileageLogIds) {
      const ref = doc(db, MileageLog.getPath(logId));
      batch.update(ref, {
        status: "archived",
        modified: now,
        modifiedBy: state.user.id,
      });
    }
    await batch.commit();

    // Update local state
    for (const logId of expense.data.mileageLogIds) {
      const existing = state.mileageLogs[logId];
      if (existing) {
        const updated = new MileageLog(logId, {
          ...existing.data,
          status: "archived",
          modified: now,
          modifiedBy: state.user.id,
        });
        commit("SET", {
          stateKey: "mileageLogs",
          stateID: logId,
          doc: updated,
        });
      }
    }
  }

  return expense;
};

export const DELETE_EXPENSE = async ({ commit }, id) => {
  // Delete receipt files from storage
  try {
    const folderRef = storageRef(storage, `expenses/${id}`);
    const files = await listAll(folderRef);
    await Promise.all(files.items.map((file) => deleteObject(file)));
  } catch (error) {
    console.error("Error deleting expense files:", error);
  }

  await deleteDoc(doc(db, Expense.getPath(id)));
  commit("REMOVE", { stateKey: "expenses", id });

  // If the deleted expense was reviewed and linked to a sale, the `touchExpense`
  // Cloud Function (functions/expenses.js) recalculates the sale's
  // `total.additionalCost` (and commission `actualCost`) server-side.
};
